import io from "socket.io-client";
import { useSyncExternalStore } from "react";
import ApiService from "../api/apiService";
import getCookie from "./getToken";
import { handleRefreshToken, isTokenExpired } from "./accountHandler";
import notifSound from "../sounds/notif-sound.mp3";
import buildhubImage from "../images/buildhub.png";

/**
 * Customer-support (Logi live agent) state for CS agents.
 *
 * One /support-agent socket per tab, shared by the sidebar badge and the
 * /support page through useSupport(). Nothing connects unless the server says
 * the current user has cs_access, so other users never open the socket.
 */

const TYPING_THROTTLE_MS = 2000;
const TYPING_CLEAR_MS = 5000;

const INITIAL_STATE = {
  initialized: false,
  enabled: false, // current user has cs_access
  me: null, // { id, display_name, cs_supervisor, admin_access }
  connected: false,
  myStatus: "online",
  sessions: {}, // id -> session (agent view)
  messages: {}, // id -> message[]
  hasMore: {}, // id -> boolean
  unread: {}, // id -> number
  customerTyping: {}, // id -> boolean
  agentTyping: {}, // id -> display_name | null
  presence: [], // [{ user_id, status }]
  missed: [],
  history: [],
  viewingSessionId: null,
};

let state = { ...INITIAL_STATE };
let socket = null;
let initPromise = null;
let lastTypingSentAt = 0;
let triedRefresh = false;
const typingTimers = {};
const listeners = new Set();

function setState(patch) {
  state = { ...state, ...(typeof patch === "function" ? patch(state) : patch) };
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useSupport() {
  return useSyncExternalStore(subscribe, () => state);
}

export const getSupportState = () => state;

// ---------------- Derived lists ----------------

const byQueuedAsc = (a, b) => new Date(a.queued_at) - new Date(b.queued_at);
const byLastMessageDesc = (a, b) =>
  new Date(b.last_message_at || b.claimed_at || 0) - new Date(a.last_message_at || a.claimed_at || 0);

export const isParticipant = (session, userId) =>
  Boolean(session?.agents?.some((agent) => agent.user_id === userId));

export const myRole = (session, userId) =>
  session?.agents?.find((agent) => agent.user_id === userId)?.role || null;

export function selectLists(s) {
  const all = Object.values(s.sessions);
  const myId = s.me?.id;
  const active = all.filter((x) => x.status === "active").sort(byLastMessageDesc);
  return {
    queue: all.filter((x) => x.status === "waiting").sort(byQueuedAsc),
    mine: active.filter((x) => myRole(x, myId) === "owner"),
    assisting: active.filter((x) => myRole(x, myId) === "assistant"),
    otherActive: active.filter((x) => !isParticipant(x, myId)),
  };
}

export function selectBadgeCount(s) {
  const { queue } = selectLists(s);
  const unread = Object.entries(s.unread).reduce(
    (sum, [id, n]) => sum + (isParticipant(s.sessions[id], s.me?.id) ? n : 0),
    0,
  );
  return queue.length + unread;
}

// ---------------- Notifications ----------------

function playSound() {
  try {
    const audio = new Audio(notifSound);
    audio.play().catch(() => {});
  } catch {
    /* audio unavailable */
  }
}

function notify(title, body, sessionId) {
  playSound();
  if (typeof window === "undefined" || !("Notification" in window)) return;
  // Only interrupt when the agent is not already looking at this chat.
  if (document.visibilityState === "visible" && state.viewingSessionId === sessionId) return;

  const show = () => {
    try {
      const notification = new Notification(title, {
        body,
        icon: buildhubImage,
        tag: sessionId ? `support-${sessionId}` : undefined,
        silent: true,
      });
      notification.onclick = () => {
        window.focus();
        window.location.assign(`/support?session=${sessionId}`);
        notification.close();
      };
    } catch {
      /* unsupported environment */
    }
  };

  if (Notification.permission === "granted") show();
  else if (Notification.permission !== "denied")
    Notification.requestPermission()
      .then((permission) => permission === "granted" && show())
      .catch(() => {});
}

// ---------------- State helpers ----------------

function upsertSession(session) {
  if (!session?.id) return;
  setState((s) => ({
    sessions: { ...s.sessions, [session.id]: { ...(s.sessions[session.id] || {}), ...session } },
    unread:
      typeof session.my_unread_count === "number"
        ? { ...s.unread, [session.id]: session.my_unread_count }
        : s.unread,
  }));
}

function upsertMessage(sessionId, message) {
  const list = state.messages[sessionId];
  if (!list) return false; // not loaded yet; fetched on open
  const index = list.findIndex(
    (m) => m.id === message.id || (message.client_msg_id && m.client_msg_id === message.client_msg_id),
  );
  const next = [...list];
  if (index >= 0) next[index] = message;
  else next.push(message);
  setState((s) => ({ messages: { ...s.messages, [sessionId]: next } }));
  return index < 0;
}

function clearTyping(map, sessionId) {
  return { ...state[map], [sessionId]: map === "customerTyping" ? false : null };
}

// ---------------- Socket ----------------

async function currentToken() {
  let token = getCookie("token");
  if (!token || isTokenExpired(token)) token = (await handleRefreshToken()) || token;
  return token;
}

function attachHandlers() {
  socket.on("connect", () => {
    triedRefresh = false;
    setState({ connected: true });
  });
  socket.on("disconnect", () => setState({ connected: false }));

  socket.on("connect_error", async (err) => {
    if (err?.message === "forbidden") {
      teardown();
      setState({ enabled: false, connected: false });
      return;
    }
    if (err?.message === "unauthorized" && !triedRefresh) {
      // Access token expired between page load and (re)connect: refresh once.
      triedRefresh = true;
      await handleRefreshToken();
      socket?.connect();
    }
  });

  socket.on("support:bootstrap", (payload) => {
    const sessions = {};
    const unread = { ...state.unread };
    [...(payload.queue || []), ...(payload.my_sessions || [])].forEach((session) => {
      sessions[session.id] = session;
      if (typeof session.my_unread_count === "number") unread[session.id] = session.my_unread_count;
    });
    setState((s) => ({
      sessions: { ...s.sessions, ...sessions },
      unread,
      presence: payload.presence || [],
      myStatus: payload.me?.status || "online",
    }));
    // Team view of every active chat (for "Join to assist").
    ApiService.support.listSessions("active", 1, 50).then((res) => {
      if (res.success) res.sessions.forEach(upsertSession);
    });
    refreshList("missed");
  });

  socket.on("support:session_updated", (session) => {
    const previous = state.sessions[session.id];
    upsertSession(session);
    // Leaving, transferring or closing removes us from the session room; keep
    // watching the chat that is open on screen so it stays live.
    if (session.id === state.viewingSessionId) socket.emit("agent:watch", { sessionId: session.id });
    if (session.status === "waiting" && previous?.status !== "waiting") {
      notify("New customer waiting", session.customer_name || "Customer", session.id);
    }
    if (session.status === "missed" && previous?.status !== "missed") refreshList("missed");
  });

  socket.on("support:assigned", (session) => {
    upsertSession(session);
    notify("Chat transferred to you", session.customer_name || "Customer", session.id);
  });

  socket.on("support:message", ({ sessionId, message }) => {
    const added = upsertMessage(sessionId, message);
    const isMine = message.sender_type === "agent" && message.sender_user_id === state.me?.id;
    if (message.sender_type === "customer") setState({ customerTyping: clearTyping("customerTyping", sessionId) });
    if (message.sender_type === "agent") setState({ agentTyping: clearTyping("agentTyping", sessionId) });

    const session = state.sessions[sessionId];
    if (session && message.sender_type !== "system" && !message.is_internal_note) {
      upsertSession({ id: sessionId, last_message_at: message.createdAt, last_message_preview: message.content.slice(0, 255) });
    }
    if (isMine || message.sender_type === "system") return;

    const viewing = state.viewingSessionId === sessionId && document.visibilityState === "visible";
    if (viewing) {
      ApiService.support.markRead(sessionId);
      return;
    }
    if (isParticipant(session, state.me?.id) && (added || !state.messages[sessionId])) {
      setState((s) => ({ unread: { ...s.unread, [sessionId]: (s.unread[sessionId] || 0) + 1 } }));
      if (message.sender_type === "customer")
        notify(session?.customer_name || "Customer", message.content, sessionId);
    }
  });

  socket.on("support:customer_typing", ({ sessionId, isTyping }) => {
    clearTimeout(typingTimers[`c-${sessionId}`]);
    setState((s) => ({ customerTyping: { ...s.customerTyping, [sessionId]: !!isTyping } }));
    if (isTyping)
      typingTimers[`c-${sessionId}`] = setTimeout(
        () => setState({ customerTyping: clearTyping("customerTyping", sessionId) }),
        TYPING_CLEAR_MS,
      );
  });

  socket.on("support:agent_typing", ({ sessionId, display_name: name, isTyping }) => {
    clearTimeout(typingTimers[`a-${sessionId}`]);
    setState((s) => ({ agentTyping: { ...s.agentTyping, [sessionId]: isTyping ? name : null } }));
    if (isTyping)
      typingTimers[`a-${sessionId}`] = setTimeout(
        () => setState({ agentTyping: clearTyping("agentTyping", sessionId) }),
        TYPING_CLEAR_MS,
      );
  });

  socket.on("support:customer_presence", ({ sessionId, online }) => {
    if (state.sessions[sessionId]) upsertSession({ id: sessionId, customer_online: online });
  });

  socket.on("support:presence", ({ agents }) => setState({ presence: agents || [] }));
}

async function connect() {
  if (socket) return;
  const token = await currentToken();
  socket = io(`${process.env.REACT_APP_API_URL}/support-agent`, {
    // Read on every (re)connect so a refreshed access token is picked up.
    auth: (cb) => cb({ token: getCookie("token") || token }),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
  });
  attachHandlers();
}

function teardown() {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
}

// ---------------- Public actions ----------------

/** Check cs_access with the server (localStorage can be stale) and connect if allowed. */
export function initSupport() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const res = await ApiService.account.getUserDetails();
      const user = res?.user_details;
      const enabled = Boolean(res?.success && user?.cs_access);
      setState({
        initialized: true,
        enabled,
        me: user
          ? {
              id: user.id,
              display_name: user.display_name,
              cs_supervisor: Boolean(user.cs_supervisor),
              admin_access: Boolean(user.admin_access),
            }
          : null,
      });
      if (user) {
        // Keep the gate in localStorage current for code that reads it synchronously.
        try {
          const stored = JSON.parse(localStorage.getItem("userDetails")) || {};
          localStorage.setItem(
            "userDetails",
            JSON.stringify({ ...stored, cs_access: user.cs_access, cs_supervisor: user.cs_supervisor }),
          );
        } catch {
          /* ignore */
        }
      }
      if (enabled) await connect();
    } catch {
      setState({ initialized: true, enabled: false });
      initPromise = null;
    }
  })();
  return initPromise;
}

export function logoutSupport() {
  teardown();
  initPromise = null;
  state = { ...INITIAL_STATE };
  listeners.forEach((fn) => fn());
}

export async function refreshList(kind) {
  const res = await ApiService.support.listSessions(kind === "history" ? "history" : kind, 1, 50);
  if (!res.success) return;
  res.sessions.forEach(upsertSession);
  if (kind === "missed" || kind === "history") setState({ [kind]: res.sessions.map((x) => x.id) });
}

export async function openSession(sessionId) {
  const previous = state.viewingSessionId;
  if (previous && previous !== sessionId) closeSession(previous);
  setState((s) => ({ viewingSessionId: sessionId, unread: { ...s.unread, [sessionId]: 0 } }));
  socket?.emit("agent:watch", { sessionId });

  const [detail, history] = await Promise.all([
    ApiService.support.getSession(sessionId),
    ApiService.support.getMessages(sessionId),
  ]);
  if (detail.success) upsertSession(detail.session);
  if (history.success) {
    setState((s) => ({
      messages: { ...s.messages, [sessionId]: history.messages },
      hasMore: { ...s.hasMore, [sessionId]: history.has_more },
    }));
  }
  if (detail.success && detail.session.my_role) ApiService.support.markRead(sessionId);
  return detail;
}

export function closeSession(sessionId) {
  socket?.emit("agent:unwatch", { sessionId });
  if (state.viewingSessionId === sessionId) setState({ viewingSessionId: null });
}

export async function loadOlderMessages(sessionId) {
  const list = state.messages[sessionId] || [];
  if (!list.length || !state.hasMore[sessionId]) return;
  const res = await ApiService.support.getMessages(sessionId, list[0].id);
  if (!res.success) return;
  setState((s) => ({
    messages: { ...s.messages, [sessionId]: [...res.messages, ...(s.messages[sessionId] || [])] },
    hasMore: { ...s.hasMore, [sessionId]: res.has_more },
  }));
}

function newClientMsgId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function sendAgentMessage(sessionId, content, isInternalNote = false) {
  const text = String(content || "").trim();
  if (!text) return { success: false };
  const clientMsgId = newClientMsgId();
  const me = state.me;
  upsertMessage(sessionId, {
    id: clientMsgId,
    client_msg_id: clientMsgId,
    session_id: sessionId,
    sender_type: "agent",
    sender_user_id: me?.id,
    sender: { id: me?.id, display_name: me?.display_name },
    content: text,
    is_internal_note: isInternalNote,
    createdAt: new Date().toISOString(),
    pending: true,
  });
  sendTyping(sessionId, false);

  const res = await ApiService.support.sendMessage(sessionId, { content: text, clientMsgId, isInternalNote });
  if (res.success) upsertMessage(sessionId, res.message);
  else
    setState((s) => ({
      messages: {
        ...s.messages,
        [sessionId]: (s.messages[sessionId] || []).map((m) =>
          m.id === clientMsgId ? { ...m, pending: false, failed: true } : m,
        ),
      },
    }));
  return res;
}

export function sendTyping(sessionId, isTyping) {
  if (!socket?.connected) return;
  const now = Date.now();
  if (isTyping && now - lastTypingSentAt < TYPING_THROTTLE_MS) return;
  lastTypingSentAt = isTyping ? now : 0;
  socket.emit("agent:typing", { sessionId, isTyping });
}

export function setMyStatus(status) {
  if (!socket) return;
  socket.emit("agent:set_status", { status }, (res) => {
    if (res?.ok) setState({ myStatus: res.status });
  });
}

/** Run a session action (claim, join, …) and merge the returned session. */
export async function runSessionAction(action, sessionId, ...args) {
  const res = await ApiService.support[action](sessionId, ...args);
  if (res.success && res.session) upsertSession(res.session);
  if (res.success && !res.session) {
    const detail = await ApiService.support.getSession(sessionId);
    if (detail.success) upsertSession(detail.session);
  }
  if (res.success && ["resolve", "requeue", "reopen"].includes(action)) refreshList("missed");
  return res;
}
